import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  Save,
  Truck,
  Building,
  MapPin,
  Mail,
  Phone,
  Landmark,
  CreditCard,
  RefreshCcw,
  Check,
  Info,
  AlertCircle,
  X,
  ExternalLink
} from 'lucide-react';

/**
 * Formulario de Proveedores - Estándar Impulza (modelo Clientes)
 * - Sin popouts: mensajes/confirmaciones siempre inline
 * - El id se toma desde la URL (proveedores-nuevo / proveedores-editar/:id)
 */
const ProveedoresForm = ({ onNavigate }) => {
  const { id } = useParams();
  const { profile } = useAuth();

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!!id);
  const [isSuccess, setIsSuccess] = useState(false);
  
  // Estado para controlar si tiene movimientos (bloqueo de saldo)
  const [hasMovements, setHasMovements] = useState(false);

  // Mensajería inline
  const [uiMsg, setUiMsg] = useState(null); // {type,title,message,action?:{label,onClick}}

  // Mantengo TODOS los campos existentes (sin borrar)
  const [form, setForm] = useState({
    razon_social: '',
    cuit: '',
    email: '',
    telefono: '',
    direccion: '',
    localidad: '',
    provincia: '',
    cbu: '',
    condicion_iva: 'Responsable Inscripto',
    condicion_pago: 'Cuenta Corriente',
    saldo: 0,
    bank_name: ''
  });

  const showMsg = (type, title, message, action) => {
    setUiMsg({ type, title, message, action: action || null });
  };

  useEffect(() => {
    if (id) fetchProveedor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProveedor = async () => {
    try {
      setFetching(true);
      
      // 1. Cargar datos del proveedor
      const { data, error } = await supabase
        .from('proveedores')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      // 2. Chequear si tiene movimientos (Compras o Pagos registrados en historial)
      // Usamos 'compras' ya que ahí se guardan tanto las facturas como los pagos históricos
      const { count, error: countError } = await supabase
        .from('compras')
        .select('*', { count: 'exact', head: true })
        .eq('proveedor_id', id);
        
      if (!countError) {
        setHasMovements(count > 0);
      }

      if (data) {
        setForm((prev) => ({
          ...prev,
          razon_social: data.razon_social || '',
          cuit: data.cuit || '',
          email: data.email || '',
          telefono: data.telefono || '',
          direccion: data.direccion || '',
          localidad: prev.localidad || '',
          provincia: prev.provincia || '',
          cbu: data.cbu_alias || '',
          condicion_iva: data.tax_condition || prev.condicion_iva,
          condicion_pago: data.default_payment_condition || prev.condicion_pago,
          saldo: typeof data.saldo === 'number' ? data.saldo : parseFloat(data.saldo || 0),
          bank_name: data.bank_name || ''
        }));
      }
    } catch (error) {
      console.error(error);
      showMsg('error', 'Error', 'No se pudo sincronizar el legajo del proveedor.');
    } finally {
      setFetching(false);
    }
  };

  const kpis = useMemo(() => {
    const rs = (form.razon_social || '').trim();
    const saldo = Number(form.saldo || 0);

    return [
      {
        label: 'Modo',
        val: id ? 'Edición' : 'Alta',
        icon: <Info size={18} />,
        bg: 'bg-blue-50',
        text: 'text-blue-600'
      },
      {
        label: 'Entidad',
        val: rs ? 'Validada' : 'Pendiente',
        icon: <Building size={18} />,
        bg: rs ? 'bg-emerald-50' : 'bg-amber-50',
        text: rs ? 'text-emerald-600' : 'text-amber-600'
      },
      {
        label: 'Condición IVA',
        val: (form.condicion_iva || 'N/D').toUpperCase(),
        icon: <CreditCard size={18} />,
        bg: 'bg-slate-50',
        text: 'text-slate-600'
      },
      {
        label: 'Saldo Actual',
        val: `$ ${saldo.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`,
        icon: <Truck size={18} />,
        bg: 'bg-rose-50',
        text: 'text-rose-600'
      }
    ];
  }, [form, id]);

  const normalizeDireccion = () => {
    const base = (form.direccion || '').trim();
    const loc = (form.localidad || '').trim();
    const prov = (form.provincia || '').trim();

    if (!base && (loc || prov)) return `${loc}${loc && prov ? ', ' : ''}${prov}`.trim();
    if (!base) return '';

    const tail = [loc, prov].filter(Boolean).join(', ');
    if (!tail) return base;

    const hayLoc = loc && base.toLowerCase().includes(loc.toLowerCase());
    const hayProv = prov && base.toLowerCase().includes(prov.toLowerCase());
    if (hayLoc && hayProv) return base;

    return `${base} — ${tail}`;
  };

  const findExistingByCuit = async (cuit) => {
    if (!profile?.organization_id) return null;
    const c = String(cuit || '').trim();
    if (!c) return null;
    const { data, error } = await supabase
      .from('proveedores')
      .select('id, razon_social, cuit, is_active')
      .eq('organization_id', profile.organization_id)
      .eq('cuit', c)
      .order('created_at', { ascending: false })
      .limit(1);
    if (error) return null;
    return (data || [])[0] || null;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!profile?.organization_id || loading) return;

    setUiMsg(null);

    const rs = (form.razon_social || '').trim();
    if (!rs) return showMsg('error', 'Campos incompletos', 'Razón social obligatoria.');

    setLoading(true);
    try {
      const payload = {
        razon_social: rs.toUpperCase(),
        cuit: (form.cuit || '').trim(),
        email: (form.email || '').trim().toLowerCase(),
        telefono: (form.telefono || '').trim(),
        direccion: normalizeDireccion().toUpperCase(),
        tax_condition: (form.condicion_iva || 'Responsable Inscripto').trim(),
        default_payment_condition: (form.condicion_pago || 'Cuenta Corriente').trim(),
        cbu_alias: (form.cbu || '').trim(),
        bank_name: (form.bank_name || '').trim(),
        doc_type: 'CUIT',
        organization_id: profile.organization_id,
        // Solo enviamos saldo si estamos creando O si estamos editando pero NO tiene movimientos
        saldo: (!id || !hasMovements) ? (parseFloat(form.saldo) || 0) : undefined
      };
      
      // Limpieza extra por seguridad si es update con movimientos
      if (id && hasMovements) delete payload.saldo;

      const { data, error } = id
        ? await supabase.from('proveedores').update(payload).eq('id', id).select().single()
        : await supabase.from('proveedores').insert([payload]).select().single();

      if (error) throw error;

      setIsSuccess(true);
      showMsg('success', 'OK', id ? 'Legajo actualizado.' : 'Proveedor registrado.');

      window.setTimeout(() => {
        onNavigate('proveedores-detalle', data.id);
      }, 500);
    } catch (error) {
      console.error(error);

      // Duplicado (unique)
      const code = String(error?.code || '');
      if (code === '23505') {
        const existing = await findExistingByCuit(form.cuit);
        if (existing?.id) {
          return showMsg(
            'info',
            'Proveedor ya existe',
            `Ya existe un proveedor con ese documento (${existing.cuit || form.cuit}). ¿Querés abrir el legajo existente?`,
            {
              label: 'Abrir legajo',
              onClick: () => onNavigate('proveedores-detalle', existing.id)
            }
          );
        }
        return showMsg('error', 'Duplicado', 'Ya existe un proveedor con ese documento.');
      }

      showMsg('error', 'Error técnico', error?.message || 'No disponible');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin text-[#51A7FD] mb-4" size={40} />
        <p className="font-bold uppercase tracking-[0.3em] text-slate-400 text-[10px]">Sincronizando entidad</p>
      </div>
    );
  }

  return (
    <div className="w-full p-3 md:p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-nova-in">
      {/* HEADER */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-3 px-1">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onNavigate(id ? 'proveedores-detalle' : 'proveedores', id)}
            className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm transition-all"
            title="Volver"
          >
            <ArrowLeft size={18} />
          </button>

          <div className="p-2 bg-white border border-slate-200 rounded-lg text-slate-400 shadow-sm">
            <Truck size={18} />
          </div>

          <div>
            <h1 className="text-lg md:text-xl font-black text-slate-800 uppercase tracking-tight leading-none">
              {id ? 'Editar Proveedor' : 'Nuevo Proveedor'}
            </h1>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1 italic">
              Legajo • Parámetros fiscales y comerciales
            </p>
          </div>
        </div>

        <button
          data-onboarding-id="proveedor-submit"
          onClick={handleSave}
          disabled={loading || isSuccess}
          className={`px-4 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg ${
            isSuccess ? 'bg-[#3ff2aa] text-[#0B2A36]' : 'bg-[#3ff2aa] text-[#0B2A36] hover:scale-[1.01] active:scale-95'
          }`}
          title="Guardar"
        >
          {isSuccess ? <><Check size={16} strokeWidth={4} /> Guardado</> : loading ? <><RefreshCcw size={16} className="animate-spin" /> Guardando</> : <><Save size={16} /> Guardar</>}
        </button>
      </div>

      {/* MENSAJES INLINE */}
      {uiMsg && (
        <div
          className={`mb-3 rounded-2xl border p-3 shadow-sm flex items-start justify-between gap-3 ${
            uiMsg.type === 'error'
              ? 'bg-rose-50 border-rose-200'
              : uiMsg.type === 'success'
              ? 'bg-emerald-50 border-emerald-200'
              : 'bg-blue-50 border-blue-200'
          }`}
        >
          <div className="flex items-start gap-3">
            <div
              className={`mt-0.5 ${
                uiMsg.type === 'error' ? 'text-rose-600' : uiMsg.type === 'success' ? 'text-emerald-600' : 'text-blue-600'
              }`}
            >
              {uiMsg.type === 'error' ? <AlertCircle size={18} /> : uiMsg.type === 'success' ? <Check size={18} /> : <Info size={18} />}
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-700">{uiMsg.title}</div>
              <div className="text-[10px] font-bold text-slate-600 mt-0.5">{uiMsg.message}</div>
              {uiMsg.action && (
                <button
                  type="button"
                  onClick={uiMsg.action.onClick}
                  className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 text-[10px] font-black uppercase tracking-widest hover:border-[#51A7FD]"
                >
                  <ExternalLink size={14} /> {uiMsg.action.label}
                </button>
              )}
            </div>
          </div>
          <button type="button" onClick={() => setUiMsg(null)} className="p-2 rounded-xl bg-white/60 border border-slate-200 text-slate-500 hover:text-slate-700" title="Cerrar">
            <X size={14} />
          </button>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-3">
        {kpis.map((k) => (
          <div key={k.label} className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3 hover:shadow-md hover:border-slate-300 transition-all duration-300 group">
            <div className={`p-2 rounded-xl border border-slate-100 ${k.bg} ${k.text} group-hover:scale-105 transition-transform`}>{k.icon}</div>
            <div className="leading-tight">
              <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">{k.label}</p>
              <p className={`text-[11px] font-black uppercase ${k.text}`}>{k.val}</p>
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={handleSave} className="space-y-3">
        {/* 1. IDENTIFICACIÓN */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
          <h2 className="text-[9px] font-black text-[#51A7FD] uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
            <Building size={14} /> 1. Identificación y parámetros fiscales
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-3">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Razón social / Nombre</label>
              <input
                data-onboarding-id="proveedor-name-input"
                value={form.razon_social}
                onChange={(e) => setForm({ ...form, razon_social: e.target.value })}
                className="mt-1 w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[11px] font-black uppercase outline-none focus:border-[#51A7FD]"
                placeholder="PROVEEDOR"
                required
              />
            </div>

            <div>
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Documento</label>
              <input
                value={form.cuit}
                onChange={(e) => setForm({ ...form, cuit: e.target.value })}
                className="mt-1 w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[11px] font-black uppercase outline-none focus:border-[#51A7FD]"
                placeholder="CUIT"
              />
            </div>

            <div>
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Condición IVA</label>
              <select
                value={form.condicion_iva}
                onChange={(e) => setForm({ ...form, condicion_iva: e.target.value })}
                className="mt-1 w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[10px] font-black uppercase outline-none focus:border-[#51A7FD]"
              >
                <option>Responsable Inscripto</option>
                <option>Monotributo</option>
                <option>Exento</option>
                <option>Consumidor Final</option>
              </select>
            </div>

            <div>
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Condición de pago</label>
              <select
                value={form.condicion_pago}
                onChange={(e) => setForm({ ...form, condicion_pago: e.target.value })}
                className="mt-1 w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[10px] font-black uppercase outline-none focus:border-[#51A7FD]"
              >
                <option>Cuenta Corriente</option>
                <option>Contado</option>
                <option>7 días</option>
                <option>15 días</option>
                <option>30 días</option>
              </select>
            </div>
          </div>
        </div>

        {/* 2. CONTACTO */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
          <h2 className="text-[9px] font-black text-[#51A7FD] uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
            <MapPin size={14} /> 2. Ubicación y contacto
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-3">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Dirección</label>
              <input
                value={form.direccion}
                onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                className="mt-1 w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[11px] font-black uppercase outline-none focus:border-[#51A7FD]"
                placeholder="CALLE Y ALTURA"
              />
            </div>

            <div>
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Localidad</label>
              <input
                value={form.localidad}
                onChange={(e) => setForm({ ...form, localidad: e.target.value })}
                className="mt-1 w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[11px] font-black uppercase outline-none focus:border-[#51A7FD]"
                placeholder="CIUDAD"
              />
            </div>

            <div>
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Provincia</label>
              <input
                value={form.provincia}
                onChange={(e) => setForm({ ...form, provincia: e.target.value })}
                className="mt-1 w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[11px] font-black uppercase outline-none focus:border-[#51A7FD]"
                placeholder="PROVINCIA"
              />
            </div>

            <div>
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2"><Mail size={14} /> Email</label>
              <input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[11px] font-black outline-none focus:border-[#51A7FD]"
                placeholder="mail@proveedor.com"
              />
            </div>

            <div>
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2"><Phone size={14} /> Teléfono</label>
              <input
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                className="mt-1 w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[11px] font-black outline-none focus:border-[#51A7FD]"
                placeholder="+54 11 ..."
              />
            </div>
          </div>
        </div>

        {/* 3. DATOS BANCARIOS / APERTURA */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
          <h2 className="text-[9px] font-black text-[#51A7FD] uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
            <Landmark size={14} /> 3. Configuración comercial
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div>
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">CBU / Alias</label>
              <input
                value={form.cbu}
                onChange={(e) => setForm({ ...form, cbu: e.target.value })}
                className="mt-1 w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[11px] font-black uppercase outline-none focus:border-[#51A7FD]"
                placeholder="ALIAS.CBU"
              />
            </div>

            <div>
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Banco</label>
              <input
                value={form.bank_name}
                onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                className="mt-1 w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[11px] font-black uppercase outline-none focus:border-[#51A7FD]"
                placeholder="BANCO"
              />
            </div>

            <div>
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Saldo inicial</label>
              <input
                type="number"
                step="0.01"
                // Deshabilitado si tiene movimientos
                disabled={hasMovements}
                value={form.saldo}
                onChange={(e) => setForm({ ...form, saldo: e.target.value })}
                className={`mt-1 w-full bg-slate-50 border border-slate-100 p-3 rounded-xl text-[11px] font-black outline-none focus:border-[#51A7FD] ${hasMovements ? 'opacity-60 cursor-not-allowed' : ''}`}
              />
              {hasMovements && (
                <p className="text-[9px] font-bold text-slate-400 mt-1">Este proveedor tiene movimientos. El saldo se actualiza automáticamente.</p>
              )}
            </div>
          </div>
        </div>

        <div className="pb-4" />
      </form>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default ProveedoresForm;